import React, { useState, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import deltaLogo from '../../assets/logo.png';
import styles from './Admin.module.css';

/* ── Lazy load sections for performance ── */
const AdminUsers     = lazy(() => import('./sections/AdminUsers'));
const AdminStrategy  = lazy(() => import('./sections/AdminStrategy'));
const AdminOrders    = lazy(() => import('./sections/AdminOrders'));
const AdminAnalytics = lazy(() => import('./sections/AdminAnalytics'));
const AdminLogs      = lazy(() => import('./sections/AdminLogs'));
const AdminSignal    = lazy(() => import('./sections/AdminSignal'));

/* ── Nav items ── */
const NAV_ITEMS = [
  {
    key:   'signal',
    label: 'Signal',
    icon:  (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
      </svg>
    ),
  },
  {
    key:   'users',
    label: 'Users',
    icon:  (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    key:   'strategy',
    label: 'Strategy',
    icon:  (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
  },
  {
    key:   'orders',
    label: 'Orders',
    icon:  (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
      </svg>
    ),
  },
  {
    key:   'analytics',
    label: 'Analytics',
    icon:  (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6"  y1="20" x2="6"  y2="14"/>
      </svg>
    ),
  },
  {
    key:   'logs',
    label: 'Signal Logs',
    icon:  (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
        <polyline points="13 2 13 9 20 9"/>
      </svg>
    ),
  },
];

/* ── Section loader fallback ── */
function SectionLoader() {
  return (
    <div className={styles.sectionLoader}>
      <div className={styles.loaderSpinner} />
      <span>Loading…</span>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ADMIN SHELL
══════════════════════════════════════════════ */
export default function Admin() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const [active, setActive] = useState('users');

  const meData      = user?.data ?? user ?? {};
  const displayName = meData.name ?? meData.username ?? meData.email?.split('@')[0] ?? 'Admin';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const renderSection = () => {
    switch (active) {
      case 'signal':    return <AdminSignal />;
      case 'users':     return <AdminUsers />;
      case 'strategy':  return <AdminStrategy />;
      case 'orders':    return <AdminOrders />;
      case 'analytics': return <AdminAnalytics />;
      case 'logs':      return <AdminLogs />;
      default:          return <AdminUsers />;
    }
  };

  const activeItem = NAV_ITEMS.find(n => n.key === active);

  return (
    <div className={styles.shell}>

      {/* ── SIDEBAR ── */}
      <aside className={styles.sidebar}>

        {/* Brand */}
        <div className={styles.sidebarBrand}>
          <div className={styles.brandLogoWrap}>
            <img src={deltaLogo} alt="DeltaDesk" className={styles.brandLogo} />
          </div>
          <div>
            <div className={styles.brandName}>DeltaDesk</div>
            <div className={styles.brandTag}>Admin Portal</div>
          </div>
        </div>

        {/* Nav */}
        <nav className={styles.sidebarNav}>
          {/* Back to Dashboard — mobile only */}
          <Link to="/dashboard" className={`${styles.navItem} ${styles.backItem}`}>
            <span className={styles.navIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </span>
            <span className={styles.navLabel}>Back</span>
          </Link>
          <div className={styles.navSection}>
            <div className={styles.navSectionLabel}>Management</div>
            {NAV_ITEMS.map(item => (
              <button
                key={item.key}
                className={`${styles.navItem} ${active === item.key ? styles.navItemActive : ''}`}
                onClick={() => setActive(item.key)}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                <span className={styles.navLabel}>{item.label}</span>
                {active === item.key && <span className={styles.navActiveBar} />}
              </button>
            ))}
          </div>
        </nav>

        {/* Sidebar footer */}
        <div className={styles.sidebarFooter}>
          <div className={styles.footerUser}>
            <div className={styles.footerAvatar}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="4" fill="#00e5a0"/>
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#00e5a0" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className={styles.footerUserInfo}>
              <div className={styles.footerName}>{displayName}</div>
              <div className={styles.footerRole}>ADMIN</div>
            </div>
          </div>
          <div className={styles.footerActions}>
            <Link to="/dashboard" className={styles.footerLink}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
              </svg>
              Dashboard
            </Link>
            <button className={styles.footerLogout} onClick={handleLogout}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <div className={styles.content}>

        {/* Top bar */}
        <header className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <h1 className={styles.pageTitle}>{activeItem?.label}</h1>
            <div className={styles.pageCrumb}>
              Admin Portal › {activeItem?.label}
            </div>
          </div>
          <div className={styles.topBarRight}>
            <div className={styles.adminBadge}>
              <span className={styles.adminBadgeDot} />
              ADMIN MODE
            </div>
          </div>
        </header>

        {/* Section content */}
        <main className={styles.sectionWrap}>
          <Suspense fallback={<SectionLoader />}>
            {renderSection()}
          </Suspense>
        </main>

      </div>
    </div>
  );
}